import clsx from 'clsx'

const SCHEDULES = [
  {
    value: 'disabled',
    icon: '⏸',
    label: 'Manual only',
    desc: 'Generate only when triggered manually.',
  },
  {
    value: 'on_push',
    icon: '⚡',
    label: 'On every push',
    desc: 'Trigger generation whenever commits land on the watched branch.',
    requiresWebhook: true,
  },
  {
    value: 'on_merge',
    icon: '🔀',
    label: 'On PR / MR merge',
    desc: 'Generate when a pull request is merged into the watched branch.',
    requiresWebhook: true,
  },
  {
    value: 'daily',
    icon: '📅',
    label: 'Daily',
    desc: 'Regenerate once per day at a chosen UTC time.',
  },
  {
    value: 'weekly',
    icon: '📆',
    label: 'Weekly',
    desc: 'Regenerate once per week on a chosen day and time.',
  },
]

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/**
 * SchedulePicker
 *
 * Props:
 *   schedule    — current schedule value
 *   scheduleTime — 'HH:MM' UTC
 *   scheduleDay  — 'monday' etc.
 *   onChange    — ({ schedule, schedule_time, schedule_day }) => void
 *   disabled    — bool
 */
export default function SchedulePicker({ schedule, scheduleTime, scheduleDay, onChange, disabled = false }) {
  const set = (key, val) => {
    onChange({
      auto_generate_schedule: schedule,
      schedule_time: scheduleTime,
      schedule_day:  scheduleDay,
      [key]: val,
    })
  }

  const currentSchedule = SCHEDULES.find(s => s.value === schedule) ?? SCHEDULES[0]

  return (
    <div className="space-y-4">
      {/* Schedule cards */}
      <div className="grid grid-cols-1 gap-2">
        {SCHEDULES.map(s => (
          <button
            key={s.value}
            type="button"
            disabled={disabled}
            onClick={() => set('auto_generate_schedule', s.value)}
            className={clsx(
              'flex items-start gap-3.5 px-4 py-3.5 rounded-xl border text-left transition-all duration-200',
              schedule === s.value
                ? 'bg-purple-500/15 border-purple-500/40 shadow-glow-violet/20'
                : 'bg-white/[0.03] border-white/8 hover:bg-white/[0.06] hover:border-white/15',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {/* Radio dot */}
            <div className={clsx(
              'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
              schedule === s.value ? 'border-purple-500 bg-purple-500' : 'border-white/25'
            )}>
              {schedule === s.value && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-semibold text-white">{s.icon} {s.label}</span>
                {s.requiresWebhook && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded-full border border-amber-500/20">
                    webhook
                  </span>
                )}
              </div>
              <p className="text-xs text-white/45 leading-relaxed">{s.desc}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Conditional time/day pickers */}
      {(schedule === 'daily' || schedule === 'weekly') && (
        <div className="space-y-4 pl-1 pt-1 animate-fade-in">
          {/* Time picker */}
          <div>
            <label className="label-specze">Time (UTC)</label>
            <div className="relative w-40">
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400/60 pointer-events-none"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <input
                type="time"
                value={scheduleTime || '08:00'}
                onChange={e => set('schedule_time', e.target.value)}
                disabled={disabled}
                className="input-specze pl-10 w-full font-mono"
              />
            </div>
            <p className="text-[11px] text-white/30 mt-1.5">All times in UTC. Your local offset may differ.</p>
          </div>

          {/* Day picker (weekly only) */}
          {schedule === 'weekly' && (
            <div>
              <label className="label-specze">Day of week</label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map(day => {
                  const val = day.toLowerCase()
                  const selected = scheduleDay === val
                  return (
                    <button
                      key={day}
                      type="button"
                      disabled={disabled}
                      onClick={() => set('schedule_day', val)}
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150',
                        selected
                          ? 'bg-purple-500/25 border-purple-500/50 text-purple-300'
                          : 'bg-white/4 border-white/10 text-white/50 hover:bg-white/8 hover:text-white hover:border-white/20'
                      )}
                    >
                      {day.slice(0, 3)}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Webhook warning */}
      {currentSchedule.requiresWebhook && (
        <div className="flex items-start gap-3 bg-amber-500/8 border border-amber-500/20 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <p className="text-xs text-amber-300/80 leading-relaxed">
            This schedule requires a <strong className="text-amber-300">webhook to be configured</strong> in your Git provider settings.
            Enable the webhook below and follow the setup instructions.
          </p>
        </div>
      )}
    </div>
  )
}
